/**
 * dragBottomSheetHandler
 * @param {HTMLElement} panelEl - The panel element to drag
 * @param {HTMLElement} handleEl - The handle element to start drag
 * @param {Object} options - Optional: { isTouchMode, dragSenderMode, dragSenderIsDragging, iframeDocument }
 * @returns {Function} cleanup function
 */
export function dragBottomSheetHandler(panelEl, handleEl, options = {}) {
	if (!panelEl || !handleEl) return () => {};
	const { isTouchMode = { value: true }, dragSenderMode = { value: 'mid' }, dragSenderIsDragging = { value: false }, iframeDocument = document, getSnaps: getSnapsOverride = null } = options;

	const DRAG_THRESHOLD_PX = 8;
	const getSnaps = () => {
		if (typeof getSnapsOverride === 'function') return getSnapsOverride();
		const vh = window.innerHeight || iframeDocument.documentElement.clientHeight || 0;
		const topFull = 0;
		const topMid = Math.round(vh * 0.5);
		const topMini = Math.round(vh * 0.75);
		return { vh, topFull, topMid, topMini };
	};

	const modeToTop = (mode, snaps) => {
		if (mode === 'mid') return snaps.topMid;
		if (mode === 'mini') return snaps.topMini;
		return snaps.topFull;
	};

	const nearestMode = (top, snaps) => {
		const dFull = Math.abs(top - snaps.topFull);
		const dMid = Math.abs(top - snaps.topMid);
		const dMini = Math.abs(top - snaps.topMini);
		if (dFull <= dMid && dFull <= dMini) return 'full';
		if (dMid <= dFull && dMid <= dMini) return 'mid';
		return 'mini';
	};

	const getClientY = (e) => {
		if (e && e.touches && e.touches.length) return e.touches[0].clientY;
		if (e && e.changedTouches && e.changedTouches.length) return e.changedTouches[0].clientY;
		return e.clientY;
	};

	const addListeners = () => {
		iframeDocument.addEventListener('pointermove', onMove, true);
		iframeDocument.addEventListener('pointerup', onEnd, true);
		iframeDocument.addEventListener('pointercancel', onEnd, true);
		iframeDocument.addEventListener('touchmove', onMove, { passive: false, capture: true });
		iframeDocument.addEventListener('touchend', onEnd, { passive: false, capture: true });
		iframeDocument.addEventListener('touchcancel', onEnd, { passive: false, capture: true });
		iframeDocument.addEventListener('mousemove', onMove, true);
		iframeDocument.addEventListener('mouseup', onEnd, true);
	};

	const removeListeners = () => {
		iframeDocument.removeEventListener('pointermove', onMove, true);
		iframeDocument.removeEventListener('pointerup', onEnd, true);
		iframeDocument.removeEventListener('pointercancel', onEnd, true);
		iframeDocument.removeEventListener('touchmove', onMove, { capture: true });
		iframeDocument.removeEventListener('touchend', onEnd, { capture: true });
		iframeDocument.removeEventListener('touchcancel', onEnd, { capture: true });
		iframeDocument.removeEventListener('mousemove', onMove, true);
		iframeDocument.removeEventListener('mouseup', onEnd, true);
	};

	let pointerDown = false;
	let isDragging = false;
	let startY = 0;
	let startTop = 0;
	let activePointerId = null;

	const beginDrag = (e) => {
		if (isDragging) return;
		isDragging = true;
		dragSenderIsDragging.value = true;
		panelEl.style.transition = 'none';
		iframeDocument.body.style.userSelect = 'none';
		iframeDocument.body.style.webkitUserSelect = 'none';
		if (e && typeof e.pointerId === 'number' && handleEl.setPointerCapture) {
			activePointerId = e.pointerId;
			try {
				handleEl.setPointerCapture(e.pointerId);
			} catch (err) {}
		} else {
			activePointerId = null;
		}
	};

	const onMove = (e) => {
		if (!pointerDown) return;
		const snaps = getSnaps();
		const currentY = getClientY(e);
		const delta = currentY - startY;
		if (!isDragging) {
			if (Math.abs(delta) < DRAG_THRESHOLD_PX) return;
			beginDrag(e);
		}
		let nextTop = startTop + delta;
		if (nextTop < snaps.topFull) nextTop = snaps.topFull;
		if (nextTop > snaps.topMini) nextTop = snaps.topMini;
		const baseTop = modeToTop(dragSenderMode.value, snaps);
		const translate = nextTop - baseTop;
		panelEl.style.transform = `translateY(${translate}px)`;
		panelEl.style.height = `100%`;
		if (e && e.cancelable) e.preventDefault();
	};

	const onEnd = (e) => {
		if (!pointerDown) return;
		pointerDown = false;
		if (!isDragging) {
			removeListeners();
			activePointerId = null;
			return;
		}
		isDragging = false;
		setTimeout(() => {
			dragSenderIsDragging.value = false;
		}, 0);
		const snaps = getSnaps();
		const baseTop = modeToTop(dragSenderMode.value, snaps);
		const currentTranslate = parseFloat((panelEl.style.transform || '').replace('translateY(', '').replace('px)', '')) || 0;
		const currentTop = baseTop + currentTranslate;
		const nextMode = nearestMode(currentTop, snaps);
		// Instant reset of transform/height without triggering CSS transition
		panelEl.style.transition = 'none';
		panelEl.style.transform = '';
		panelEl.style.height = '';
		// Freeze the panel at its current visual position (top in px)
		panelEl.style.top = `${currentTop}px`;
		// Force reflow so the browser commits this as the transition start state
		panelEl.getBoundingClientRect(); // eslint-disable-line no-unused-expressions
		// Re-enable CSS transitions then animate from frozen position to target mode
		panelEl.style.transition = '';
		panelEl.style.top = '';
		dragSenderMode.value = nextMode;
		iframeDocument.body.style.userSelect = '';
		iframeDocument.body.style.webkitUserSelect = '';
		if (activePointerId !== null && handleEl.releasePointerCapture) {
			try {
				handleEl.releasePointerCapture(activePointerId);
			} catch (err) {}
		}
		activePointerId = null;
		removeListeners();
		if (e && e.cancelable) e.preventDefault();
	};

	const onStart = (e) => {
		if (!isTouchMode.value) return;
		pointerDown = true;
		isDragging = false;
		startY = getClientY(e);
		const snaps = getSnaps();
		startTop = modeToTop(dragSenderMode.value, snaps);
		dragSenderIsDragging.value = false;
		addListeners();
		// if (e && e.cancelable && e.type === 'touchstart') e.preventDefault();
	};

	handleEl.addEventListener('pointerdown', onStart, { passive: true });
	handleEl.addEventListener('touchstart', onStart, { passive: false });
	handleEl.addEventListener('mousedown', onStart, { passive: true });

	const cleanup = () => {
		handleEl.removeEventListener('pointerdown', onStart);
		handleEl.removeEventListener('touchstart', onStart);
		handleEl.removeEventListener('mousedown', onStart);
		removeListeners();
		pointerDown = false;
		isDragging = false;
		activePointerId = null;
		panelEl.style.transition = '';
		panelEl.style.transform = '';
		panelEl.style.height = '';
		iframeDocument.body.style.userSelect = '';
		iframeDocument.body.style.webkitUserSelect = '';
	};

	cleanup.setMode = (mode) => {
		if (dragSenderMode.value === mode) return;
		dragSenderMode.value = mode;
		panelEl.style.transition = '';
		panelEl.style.transform = '';
		panelEl.style.height = '';
	};
	return cleanup;
}
